export type PdfName = { readonly name: string }

export type PdfRef = { readonly ref: number }

export type PdfDict = ReadonlyMap<string, PdfValue>

export type PdfValue =
    | number
    | string
    | boolean
    | null
    | PdfName
    | PdfRef
    | readonly PdfValue[]
    | PdfDict

const WHITESPACE = " \t\r\n\f\0"
const DELIMITERS = "()<>[]{}/%"
const LINE_BREAKS = "\r\n"
const OBJECT_HEADER = /(\d+)[ \t\r\n]+(\d+)[ \t\r\n]+obj\b/g
const BINARY_CHUNK = 0x8000
const MAX_INFLATED_BYTES = 256 * 1024 * 1024
const ESCAPE_SHORTHANDS: Record<string, string> = { n: "\n", r: "\r", t: "\t", b: "\b", f: "\f" }

const isDigit = (character: string) => character >= "0" && character <= "9"

const isUnsignedInteger = (token: string) => token.length > 0 && token.split("").every(isDigit)

const isRegular = (character: string) =>
    !WHITESPACE.includes(character) && !DELIMITERS.includes(character)

const isDict = (value: PdfValue): value is PdfDict => value instanceof Map

const isArray = (value: PdfValue): value is readonly PdfValue[] => Array.isArray(value)

const isName = (value: PdfValue): value is PdfName =>
    typeof value === "object" && value !== null && "name" in value

const isRef = (value: PdfValue): value is PdfRef =>
    typeof value === "object" && value !== null && "ref" in value

const toBinaryString = (bytes: Uint8Array) => {
    let text = ""
    for (let start = 0; start < bytes.length; start += BINARY_CHUNK) {
        text += String.fromCharCode(...bytes.subarray(start, start + BINARY_CHUNK))
    }
    return text
}

const concat = (chunks: Uint8Array[], length: number) => {
    const joined = new Uint8Array(length)
    let offset = 0
    for (const chunk of chunks) {
        joined.set(chunk, offset)
        offset += chunk.length
    }
    return joined
}

const inflate = async (bytes: Uint8Array) => {
    const decompressor = new DecompressionStream("deflate")
    const writer = decompressor.writable.getWriter()
    const owned = new Uint8Array(bytes.length)
    owned.set(bytes)
    const writing = writer
        .write(owned)
        .then(() => writer.close())
        .catch(() => undefined)

    const reader = decompressor.readable.getReader()
    const chunks: Uint8Array[] = []
    let length = 0
    try {
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            length += value.length
            if (length > MAX_INFLATED_BYTES) {
                await reader.cancel()
                return null
            }
            chunks.push(value)
        }
    } catch {
        return null
    } finally {
        await writing
    }
    return concat(chunks, length)
}

const closesStream = (source: string, position: number) => {
    let cursor = position
    if (source[cursor] === "\r") cursor++
    if (source[cursor] === "\n") cursor++
    return source.startsWith("endstream", cursor)
}

const trimLineBreak = (source: string, start: number, end: number) => {
    let cursor = end
    if (source[cursor - 1] === "\n") cursor--
    if (source[cursor - 1] === "\r") cursor--
    return end < start ? end : Math.max(start, cursor)
}

const createCursor = (source: string, start: number) => {
    let position = start

    const skipBlanks = () => {
        for (;;) {
            while (position < source.length && WHITESPACE.includes(source[position])) position++
            if (source[position] !== "%") return
            while (position < source.length && !LINE_BREAKS.includes(source[position])) position++
        }
    }

    const readName = () => {
        position++
        let name = ""
        while (position < source.length && isRegular(source[position])) {
            if (source[position] === "#") {
                name += String.fromCharCode(parseInt(source.slice(position + 1, position + 3), 16))
                position += 3
                continue
            }
            name += source[position]
            position++
        }
        return name
    }

    const readEscape = () => {
        const character = source[position++]
        if (ESCAPE_SHORTHANDS[character]) return ESCAPE_SHORTHANDS[character]
        if (character === "\n") return ""
        if (character === "\r") {
            if (source[position] === "\n") position++
            return ""
        }
        if (!isDigit(character) || character > "7") return character
        let octal = character
        while (
            octal.length < 3 &&
            position < source.length &&
            source[position] <= "7" &&
            isDigit(source[position])
        ) {
            octal += source[position++]
        }
        return String.fromCharCode(parseInt(octal, 8))
    }

    const readLiteralString = () => {
        position++
        let depth = 1
        let text = ""
        while (position < source.length) {
            const character = source[position++]
            if (character === "\\") {
                text += readEscape()
                continue
            }
            if (character === "(") depth++
            if (character === ")" && --depth === 0) break
            text += character
        }
        return text
    }

    const readHexString = () => {
        position++
        let digits = ""
        while (position < source.length && source[position] !== ">") {
            const character = source[position++]
            if (!WHITESPACE.includes(character)) digits += character
        }
        position++
        const padded = digits.length % 2 === 0 ? digits : `${digits}0`
        let text = ""
        for (let index = 0; index < padded.length; index += 2) {
            text += String.fromCharCode(parseInt(padded.slice(index, index + 2), 16))
        }
        return text
    }

    const readReferenceTail = (token: string) => {
        if (!isUnsignedInteger(token)) return null
        const rewind = position
        skipBlanks()
        const generationStart = position
        while (position < source.length && isDigit(source[position])) position++
        if (position > generationStart) {
            skipBlanks()
            const followsReference =
                source[position] === "R" &&
                (position + 1 >= source.length || !isRegular(source[position + 1]))
            if (followsReference) {
                position++
                return { ref: Number(token) }
            }
        }
        position = rewind
        return null
    }

    const readNumberOrKeyword = () => {
        const start = position
        while (position < source.length && isRegular(source[position])) position++
        if (position === start) {
            position++
            return null
        }
        const token = source.slice(start, position)
        if (token === "true") return true
        if (token === "false") return false
        if (token === "null") return null
        return readReferenceTail(token) ?? Number(token)
    }

    const readDict = () => {
        position += 2
        const entries = new Map<string, PdfValue>()
        for (;;) {
            skipBlanks()
            if (position >= source.length || source.startsWith(">>", position)) {
                position += 2
                return entries
            }
            if (source[position] !== "/") {
                readValue()
                continue
            }
            entries.set(readName(), readValue())
        }
    }

    const readArray = () => {
        position++
        const items: PdfValue[] = []
        for (;;) {
            skipBlanks()
            if (position >= source.length || source[position] === "]") {
                position++
                return items
            }
            items.push(readValue())
        }
    }

    const readValue = (): PdfValue => {
        skipBlanks()
        const character = source[position]
        if (character === "<") return source[position + 1] === "<" ? readDict() : readHexString()
        if (character === "[") return readArray()
        if (character === "(") return readLiteralString()
        if (character === "/") return { name: readName() }
        return readNumberOrKeyword()
    }

    return {
        readValue,
        skipBlanks,
        advance: (count: number) => {
            position += count
        },
        startsWith: (token: string) => source.startsWith(token, position),
        get position() {
            return position
        },
    }
}

export const readPdfObjects = async (pdf: Uint8Array) => {
    const source = toBinaryString(pdf)
    const objects = new Map<number, { offset: number; value: PdfValue }>()
    const streams: { dict: PdfDict; start: number; end: number; offset: number }[] = []

    const remember = (objectNumber: number, offset: number, value: PdfValue) => {
        const existing = objects.get(objectNumber)
        if (!existing || offset >= existing.offset) objects.set(objectNumber, { offset, value })
    }

    const resolve = (value: PdfValue) => {
        const visited = new Set<number>()
        let current = value
        while (isRef(current)) {
            if (visited.has(current.ref)) return null
            visited.add(current.ref)
            current = objects.get(current.ref)?.value ?? null
        }
        return current
    }

    const entry = (dict: PdfDict, key: string) => resolve(dict.get(key) ?? null)
    const asDict = (value: PdfValue) => (isDict(value) ? value : null)
    const asDicts = (value: PdfValue) => (isArray(value) ? value.map(resolve).filter(isDict) : [])
    const asName = (value: PdfValue) => (isName(value) ? value.name : null)
    const asText = (value: PdfValue) => (typeof value === "string" ? value : null)
    const asNumber = (value: PdfValue) => {
        if (typeof value === "number") return value
        if (typeof value !== "string" || value.trim().length === 0) return null
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
    }
    const asNumbers = (value: PdfValue) =>
        isArray(value)
            ? value
                  .map(resolve)
                  .map(asNumber)
                  .filter((item): item is number => item !== null)
            : []

    for (const header of source.matchAll(OBJECT_HEADER)) {
        const cursor = createCursor(source, header.index + header[0].length)
        const value = cursor.readValue()
        remember(Number(header[1]), header.index, value)

        cursor.skipBlanks()
        if (!cursor.startsWith("stream")) continue
        cursor.advance("stream".length)

        let start = cursor.position
        if (source[start] === "\r") start++
        if (source[start] === "\n") start++

        const dict = asDict(value)
        if (!dict) continue
        const declaredLength = asNumber(entry(dict, "Length"))
        const closesAtDeclared =
            declaredLength !== null && closesStream(source, start + declaredLength)
        const end = closesAtDeclared
            ? start + declaredLength
            : trimLineBreak(source, start, source.indexOf("endstream", start))
        if (end < start) continue
        streams.push({ dict, start, end, offset: header.index })
    }

    for (const stream of streams) {
        if (asName(entry(stream.dict, "Type")) !== "ObjStm") continue
        const inflated = await inflate(pdf.subarray(stream.start, stream.end)).catch(() => null)
        if (!inflated) continue

        const data = toBinaryString(inflated)
        const count = asNumber(entry(stream.dict, "N")) ?? 0
        const firstOffset = asNumber(entry(stream.dict, "First")) ?? 0
        const heading = createCursor(data, 0)
        const placements: { objectNumber: number; offset: number }[] = []
        for (let index = 0; index < count && heading.position < data.length; index++) {
            placements.push({
                objectNumber: asNumber(heading.readValue()) ?? 0,
                offset: asNumber(heading.readValue()) ?? 0,
            })
        }
        for (const placement of placements) {
            const cursor = createCursor(data, firstOffset + placement.offset)
            remember(placement.objectNumber, stream.offset, cursor.readValue())
        }
    }

    let root: PdfDict | null = null
    let rootOffset = -1
    for (const { offset, value } of objects.values()) {
        const dict = asDict(value)
        if (!dict || asName(entry(dict, "Type")) !== "Catalog" || offset < rootOffset) continue
        root = dict
        rootOffset = offset
    }

    return { root, entry, asDict, asDicts, asName, asText, asNumber, asNumbers }
}

export type PdfDocument = Awaited<ReturnType<typeof readPdfObjects>>
